/**
 * File: StatStrip.tsx
 * Path: apps/admin/src/modules/layout/components/StatStrip.tsx
 * Description: A row of stat cards. On a phone the cards sit side by side in a
 * swipeable carousel (each snaps into place, the next one peeks in from the
 * right); from the `sm` breakpoint up it is the grid the page already had.
 * Every page's stat cards use this, so they behave the same everywhere.
 */

import React from "react";
import { cn } from "@idk/utils";

type StripProps = {
  /** `dl` for term/value cards, `div` for anything else. */
  as?: "dl" | "div";
  /**
   * "joined": cards share thin dividers on larger screens (the key-number
   * strips). "cards": each card keeps its own border (the tiles).
   */
  variant?: "joined" | "cards";
  /** The grid from `sm` up, as literal Tailwind classes, e.g. "sm:grid-cols-3 lg:grid-cols-6". */
  desktop: string;
  label?: string;
  role?: string;
  className?: string;
  children: React.ReactNode;
};

export const StatStrip: React.FC<StripProps> = ({
  as: Tag = "div",
  variant = "joined",
  desktop,
  label,
  role,
  className,
  children,
}) => (
  <Tag
    aria-label={label}
    role={role}
    // A scrollable region must be reachable by keyboard.
    tabIndex={0}
    className={cn(
      "flex snap-x snap-mandatory gap-stack-xs overflow-x-auto scroll-pl-0 [&>*]:w-4/5 [&>*]:shrink-0 [&>*]:snap-start",
      "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
      "sm:grid sm:overflow-visible sm:[&>*]:w-auto sm:[&>*]:shrink",
      variant === "joined"
        ? "[&>*]:border [&>*]:border-border/50 sm:gap-px sm:border sm:border-border/50 sm:bg-border/50 sm:[&>*]:border-0"
        : "sm:gap-stack-sm",
      desktop,
      className,
    )}
  >
    {children}
  </Tag>
);
